'use client';

import React, { useRef, useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import {
  Sparkles,
  ArrowRight,
  ShieldCheck,
  HeartHandshake,
  Flame,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import type { Product } from '@/data/mockData';
import { useCommerce } from '@/context/CommerceContext';
import ProductCard from '@/components/ProductCard';

interface SpotlightProductItem {
  product: Product;
  customImage?: string;
}

export default function TraditionSpotlightSection() {
  const { products } = useCommerce();
  const activeCatalog = products;
  const videoRef = useRef<HTMLVideoElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Mouse drag states for desktop
  const isMouseDownRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  // Clean one-time video playback initialization
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.defaultMuted = true;
      video.muted = true;
      video.playsInline = true;
      video.play().catch(() => {});
    }
  }, []);

  // Update carousel scroll navigation buttons status
  const updateScrollButtons = useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 15);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 15);
    }
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (el) {
      el.addEventListener('scroll', updateScrollButtons, { passive: true });
      updateScrollButtons();
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        el.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, [updateScrollButtons]);

  // Smooth scroll carousel on arrow click
  const scrollCarousel = (direction: 'left' | 'right') => {
    if (!carouselRef.current) return;
    const container = carouselRef.current;
    const card = container.querySelector<HTMLElement>('[data-carousel-card]');
    if (!card) return;
    const gap = 16;
    const scrollAmount = card.offsetWidth + gap;
    const target = direction === 'left'
      ? Math.max(0, container.scrollLeft - scrollAmount)
      : Math.min(container.scrollWidth - container.clientWidth, container.scrollLeft + scrollAmount);

    container.scrollTo({ left: target, behavior: 'smooth' });
  };

  // Fluid Mouse Drag-to-Scroll for Desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!carouselRef.current) return;
    isMouseDownRef.current = true;
    startXRef.current = e.pageX - carouselRef.current.offsetLeft;
    scrollLeftRef.current = carouselRef.current.scrollLeft;
    carouselRef.current.style.scrollBehavior = 'auto';
    carouselRef.current.style.scrollSnapType = 'none';
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMouseDownRef.current || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 1.4;
    carouselRef.current.scrollLeft = scrollLeftRef.current - walk;
  };

  const handleMouseUpOrLeave = () => {
    if (!carouselRef.current) return;
    isMouseDownRef.current = false;
    carouselRef.current.style.scrollBehavior = 'smooth';
    carouselRef.current.style.scrollSnapType = 'x proximity';
  };

  // Curated signature South Indian artisanal products for the horizontal carousel
  const spotlightProducts: SpotlightProductItem[] = activeCatalog.slice(0, 10).map((product) => ({
    product,
  }));

  if (activeCatalog.length === 0) {
    return null;
  }

  return (
    <section
      id="tradition-spotlight"
      className="w-full py-12 sm:py-16 lg:py-20 bg-white border-b border-neutral-200/80 overflow-hidden"
    >
      <div className="w-full max-w-[1560px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        
        {/* Main Split Grid: Scrolling Products Carousel on Left (8 cols) + Full-Size Video on Right (4 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 xl:gap-12 items-center">
          
          {/* Left Column: Header + Scrolling Carousel + Value Badges (7-8 cols on desktop) */}
          <div className="lg:col-span-7 xl:col-span-8 flex flex-col justify-between space-y-6 sm:space-y-7 min-w-0">
            
            {/* 1. Header Row + Left/Right Arrow Navigation Controls */}
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
              <div className="space-y-2.5 max-w-xl">
                <div className="inline-flex items-center space-x-2 px-3.5 py-1 rounded-full bg-[#950E09]/10 text-[#950E09] text-xs font-bold uppercase tracking-wider border border-[#950E09]/20">
                  <Sparkles className="w-3.5 h-3.5 text-[#950E09]" />
                  <span>Heritage Kitchen Spotlight • Daily Fresh Batches</span>
                </div>

                <h2 className="font-display font-semibold text-2xl sm:text-3xl lg:text-4xl text-[#0F382A] tracking-tight leading-[1.18]">
                  Authentic South Indian Crunch, <br className="hidden sm:inline" />
                  Handcrafted for Every Gathering
                </h2>

                <p className="text-xs sm:text-sm md:text-[14px] text-neutral-600 font-sans leading-relaxed">
                  Before grand festival banquets, every home savours the pure bliss of evening tea and freshly fried savouries. Meticulously hand-pressed in farm butter and 100% pure cold-pressed oils.
                </p>
              </div>

              {/* Left & Right Arrow Navigation Buttons */}
              <div className="flex items-center space-x-2 shrink-0 self-start sm:self-end pt-1">
                <button
                  onClick={() => scrollCarousel('left')}
                  disabled={!canScrollLeft}
                  className={`w-10 h-10 rounded-full border transition-all flex items-center justify-center cursor-pointer active:scale-95 ${
                    canScrollLeft
                      ? 'bg-white border-neutral-300 text-[#0F382A] hover:bg-[#0F382A] hover:text-white hover:border-[#0F382A] shadow-xs'
                      : 'bg-neutral-100 border-neutral-200 text-neutral-400 cursor-not-allowed opacity-60'
                  }`}
                  aria-label="Scroll products left"
                  title="Previous products"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => scrollCarousel('right')}
                  disabled={!canScrollRight}
                  className={`w-10 h-10 rounded-full border transition-all flex items-center justify-center cursor-pointer active:scale-95 ${
                    canScrollRight
                      ? 'bg-white border-neutral-300 text-[#0F382A] hover:bg-[#0F382A] hover:text-white hover:border-[#0F382A] shadow-xs'
                      : 'bg-neutral-100 border-neutral-200 text-neutral-400 cursor-not-allowed opacity-60'
                  }`}
                  aria-label="Scroll products right"
                  title="Next products"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* 2. Scrolling Horizontal Carousel Track (Fits exactly 3 cards on desktop without partial 4th card overlap) */}
            <div className="relative w-full overflow-hidden">
              <div
                ref={carouselRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUpOrLeave}
                onMouseLeave={handleMouseUpOrLeave}
                className="flex items-start gap-4 overflow-x-auto scroll-smooth scrollbar-none py-2 px-0.5 snap-x snap-mandatory touch-pan-x cursor-grab active:cursor-grabbing select-none"
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch',
                  overscrollBehaviorX: 'contain',
                }}
              >
                {spotlightProducts.map((item, idx) => (
                  <div
                    key={item.product.id}
                    data-carousel-card
                    className="w-[85%] sm:w-[calc((100%-1rem)/2)] lg:w-[calc((100%-2rem)/3)] shrink-0 snap-start"
                  >
                    <ProductCard
                      product={item.product}
                      index={idx}
                      customImage={item.customImage}
                      className="w-full"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* 3. Bottom Value Badges & View All Link */}
            <div className="pt-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-neutral-200/60">
              <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-xs font-semibold text-neutral-700">
                <span className="inline-flex items-center space-x-1.5">
                  <ShieldCheck className="w-4 h-4 text-[#52A05B]" />
                  <span>100% Cold-Pressed Oils</span>
                </span>
                <span className="inline-flex items-center space-x-1.5">
                  <Flame className="w-4 h-4 text-[#E09F55]" />
                  <span>Zero Palm Oil</span>
                </span>
                <span className="inline-flex items-center space-x-1.5">
                  <HeartHandshake className="w-4 h-4 text-[#950E09]" />
                  <span>Preservative Free</span>
                </span>
              </div>

              <Link
                href="/shop"
                className="group inline-flex items-center space-x-2 text-xs sm:text-sm font-bold text-[#0F382A] hover:text-[#950E09] transition-colors shrink-0"
              >
                <span>Explore All 25+ Varieties</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

          </div>

          {/* Right Column: Full-Size Pure Video with exact native 4:5 aspect ratio */}
          <div className="lg:col-span-5 xl:col-span-4 flex items-center justify-center w-full h-full">
            <div className="relative w-full aspect-[4/5] max-w-[480px] lg:max-w-none rounded-3xl overflow-hidden flex items-center justify-center bg-transparent">
              <video
                ref={videoRef}
                src="/videos/loop_video.mp4"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                disablePictureInPicture
                disableRemotePlayback
                className="w-full h-full object-cover rounded-3xl pointer-events-none select-none"
              />
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
